import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle, faHome, faInfoCircle, faLongArrowAltLeft, faQuestionCircle, faSignOut, faSync, faUndo, faUserGraduate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TrainingPopup, UserMenu } from 'components';
import { ActionButton } from 'components/action-button/action-button';
import { ConfirmationPopup, HelpPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { DbWorkerMessageType } from 'contexts/db-state/db-worker';
import Button from 'devextreme-react/button';
import { Popover } from 'devextreme-react/popover';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import notify from 'devextreme/ui/notify';
import React, { FC, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLoadManagerService, useNavigation, useSettingService } from 'services';
import { SyncingStatus } from 'services/enums/syncingStatus';
import { SyncStatus } from 'services/types';
import { dbWorker, isOnline, useScreenSize } from 'utils';
import './header.scss';
import { envConfig } from "config"


type Props = {
  menuToggleEnabled: boolean,
  title: string,
  backButtonVisible: boolean,
  toggleMenu: () => void
}

export const Header: FC<Props> = ({ menuToggleEnabled, backButtonVisible, title, toggleMenu }) => {

  const config = envConfig();
  const [isTrainingPopupVisible, setIsTrainingPopupVisible] = useState<boolean>(false)
  const [isSyncPopoverVisible, setIsSyncPopoverVisible] = useState<boolean>(false)
  const [isHelpMenuVisible, setIsHelpMenuVisible] = useState<boolean>(false)
  const [isTrainingModeOrDemoLoad, setIsTrainingModeOrDemoLoad] = useState<boolean>(false)

  const { isLarge } = useScreenSize();
  const navigation = useNavigation();
  const { syncRequests } = useLoadManagerService()
  const navigate = useNavigate();
  const tripContext = useContext(TripContext)

  const { getTrainingMode, setTrainingMode } = useSettingService()

  const isTrainingMode: boolean = getTrainingMode()
  const failedRequests: SyncStatus[] = tripContext.syncStatus.filter(x => x.status == SyncingStatus.FailedToSync) ?? []
  const appColor: string = isTrainingMode ? '#F783AC' : '#0066A4'
  const environment: string = config.REACT_APP_ENVIRONMENT;

  const userMenuItems = [
    {
      text: 'Help',
      icon: faQuestionCircle as IconProp,
      iconColor: '#2e75b',
      onItemClick: () => setIsHelpMenuVisible(true)
    },
    {
      text: 'Sign out',
      icon: faSignOut as IconProp,
      onItemClick: async () => {
        if (await isOnline()) {
          setTrainingMode(false);
          if (isTrainingMode) {
            await dbWorker.postMessage({ type: DbWorkerMessageType.DeleteDb })
          }
          navigate('/logout')
        }
        else {
          notify('You must be online to sign out', 'info', 3000)
        }

      }
    },
    {
      text: `v${process.env.REACT_APP_VERSION}${environment ? ` (${environment})` : ''}`,
      icon: faInfoCircle as IconProp,
      onItemClick: () => { }
    }
  ]

  if (backButtonVisible) {
    userMenuItems.unshift({
      text: `Home`,
      icon: faHome as IconProp,
      onItemClick: async () => {
        navigate('/load-assignments')
      }
    })
  }

  if (!isTrainingMode && !backButtonVisible) {
    userMenuItems.unshift({
      text: `Training Mode`,
      icon: faUserGraduate as IconProp,
      onItemClick: async () => {
        setIsTrainingModeOrDemoLoad(true)
      }
    })
  }

  if (isTrainingMode && !backButtonVisible) {
    userMenuItems.unshift({
      text: `Change Trainer`,
      icon: faUserGraduate as IconProp,
      onItemClick: async () => {
        setIsTrainingPopupVisible(true)
      }
    })
  }

  const onClickRetrieve = async (): Promise<void> => {
    navigate('/load-assignments')
  }

  const onSyncIconClick = async (): Promise<void> => {
    if (await syncRequests()) {
      notify(`Successfully Synced!`, 'success', 5000)
    } else {
      notify(`There was a problem syncing some of your changes. Ensure you have an internet connection and try again`, 'info', 5000)
    }
    setIsSyncPopoverVisible(false)
  }

  const onClickSyncPopOver = (): void => {
    setIsSyncPopoverVisible(true)
  }

  return (
    <header className={'header-component'}>
      <Toolbar onItemHold={() => setIsHelpMenuVisible(true)} itemHoldTimeout={5000} className={'header-toolbar'} elementAttr={{ class: `header-toolbar${isTrainingMode ? '-training' : ''}` }}>
        {isLarge && !backButtonVisible &&
          <Item visible={menuToggleEnabled} location={'before'} widget={'dxButton'} cssClass={'menu-button'}>
            <Button icon="menu" stylingMode="text" onClick={toggleMenu} />
          </Item>
        }
        {backButtonVisible &&

          <Item visible={true} location={'before'} widget={'dxButton'} cssClass={'menu-button'}>
            <div className='back-button-container' onClick={() => navigation.navigateBack()}>
              <FontAwesomeIcon icon={faLongArrowAltLeft as IconProp} size={'2x'} color={'white'} />
            </div>
          </Item>
        }
        <Item location={'before'} cssClass={'header-title'} text={title} visible={!!title} />
        {failedRequests.length > 0 &&
          <Item location={'after'}>
            <FontAwesomeIcon icon={faExclamationTriangle as IconProp} size={'lg'} color={'#eed202'} id={'faTriangleIcon'} onClick={onClickSyncPopOver} />
          </Item>
        }
        <Item location={'after'}>
          <UserMenu menuItems={userMenuItems} buttonColor={appColor} />
        </Item>
      </Toolbar>
      <TrainingPopup
        isVisible={isTrainingPopupVisible}
        onClickRetrieve={() => { setIsTrainingPopupVisible(false); onClickRetrieve() }}
        onClickClose={() => setIsTrainingPopupVisible(false)}
      />
      {isSyncPopoverVisible &&
        <Popover
          target="#faTriangleIcon"
          position="top"
          width={250}
          closeOnOutsideClick={true}
          onHiding={() => setIsSyncPopoverVisible(false)}
          visible={isSyncPopoverVisible}
        >
          <div className={'sync-card'}>
            You made some changes while offline that need to be synced
            <div className={'sync-card-button'}>
              <ActionButton
                icon={faSync}
                buttonColor={'#82BC24'}
                buttonText='Sync'
                onClick={onSyncIconClick}
              />
            </div>
          </div>
        </Popover>
      }
      {isTrainingModeOrDemoLoad &&
        <ConfirmationPopup
          isVisible={isTrainingModeOrDemoLoad}
          title={'Enter Training Mode?'}
          message={'Are you sure you want to enter training mode?'}
          button1={{ icon: faUndo, text: 'Cancel', backgroundColor: '#EB5757' }}
          onClickButton1={() => { setTrainingMode(false); setIsTrainingModeOrDemoLoad(false) }}
          button2={{ icon: faUserGraduate, text: 'Proceed', backgroundColor: '#82BC24' }}
          onClickButton2={() => { setIsTrainingModeOrDemoLoad(false); setIsTrainingPopupVisible(true) }}
        />
      }
      {isHelpMenuVisible &&
        <HelpPopup
          isVisible={isHelpMenuVisible}
          onClose={() => setIsHelpMenuVisible(false)}
        />
      }
      
    </header>
  );
};
